import { BookerPlatformWrapperAtomPropsForTeam } from '@calcom/atoms/dist/packages/platform/atoms/booker/types';
import { UseCreateBookingInput } from '@calcom/atoms/dist/packages/platform/atoms/hooks/bookings/useCreateBooking';
import type {
    ApiErrorResponse,
    ApiSuccessResponse,
} from '@calcom/atoms/dist/packages/platform/types/api';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { buyBooking } from 'client/widget-components/ecom/services/snipcartService';
import React, { useCallback, useContext, useMemo } from 'react';
import calApi from '../api/calApi';
import { HttpError } from '../api/errors';
import BookerBackButton from '../components/steps/BookerStep/BookerBackButton';
import BookingPricingMetaSection from '../components/steps/BookerStep/BookingPricingMetaSection';
import BookingSelectedHostMetaSection from '../components/steps/BookerStep/BookingSelectedHostMetaSection';
import { useBookingContext } from '../Context';
import {
    createBookerResponseLoggingContext,
    logBookingError,
    logBookingInfo,
} from '../loggingUtils';
import { Booking, CalBookerStep } from '../types';
import {
    bookingObjToEcomConverter,
    createDryRunBookingPayload,
    getBookerClassNames,
    getBookerMetadata,
    getRescheduleClassNames,
    shouldShowPriceMetaSection,
    shouldUsePaidBooking,
} from '../utils';
import { BookerStateContext } from './useRuntimeCalBookingState';

interface UseCommonBookerPropsParams {
    calBookerStep: CalBookerStep | null;
}

export interface CommonBookerProps
    extends BookerPlatformWrapperAtomPropsForTeam {
    rescheduleClassNames: BookerPlatformWrapperAtomPropsForTeam['customClassNames'];
    /** Native-only: price node rendered in the EventMeta details slot. */
    priceSlot?: React.ReactNode;
    /** Native-only: selected-host node rendered near the top of the EventMeta. */
    selectedProviderSlot?: React.ReactNode;
}

export const useCommonBookerProps = ({
    calBookerStep,
}: UseCommonBookerPropsParams): CommonBookerProps => {
    const bookerContext = useBookingContext();
    const editor = useEditor();
    const [bookingState, bookingActions] = useContext(BookerStateContext);

    const { teamId, designStyle, isBookingDryRun, isNativeBookerEnabled } =
        bookerContext;
    const {
        rescheduleUid,
        calEventData,
        confirmButtonDisabled,
        eventSlug,
        selectedHostId,
        isLoading,
    } = bookingState;

    const { setConfirmButtonDisabled, resetBookerState, onBookingSuccess } =
        bookingActions;

    const bookerClassNames = useMemo(
        () => getBookerClassNames(designStyle),
        [designStyle]
    );

    const rescheduleClassNames = useMemo(() => getRescheduleClassNames(), []);

    const handleBookingSuccess = useCallback(
        async (res: ApiSuccessResponse<any>) => {
            // jump to initial step of booker, no need to keep the form data around after a successful booking
            resetBookerState();

            const uid = res.data.uid;
            const seatReferenceUid = res.data.seatReferenceUid;

            try {
                if (isBookingDryRun) {
                    onBookingSuccess(createDryRunBookingPayload());
                    return;
                }

                // Atom provides not full booking data if seats are used, so we need to fetch the full booking data from the seat reference uid
                let bookingData: Booking | null = null;
                if (seatReferenceUid) {
                    bookingData = await calApi.getBookingBySeatUid(
                        seatReferenceUid
                    );
                } else {
                    bookingData = await calApi.getBooking(uid);
                }
                onBookingSuccess(bookingData);
            } catch (error: unknown) {
                logBookingError(
                    'Failed to get booking data after successful booking',
                    {
                        errorMessage: (error as Error).message,
                        additionalInfoToLog: bookerContext,
                        isSeatReferenceBooking: !!seatReferenceUid,
                        ...(error instanceof HttpError ? error : {}),
                    }
                );
            }
        },
        [onBookingSuccess, resetBookerState, bookerContext, isBookingDryRun]
    );

    const handleCreatePaidBooking = useCallback(
        async (createBookingInput: UseCreateBookingInput) => {
            try {
                setConfirmButtonDisabled(true);

                if (!editor.insideEditor() && calEventData) {
                    await buyBooking(
                        bookingObjToEcomConverter(
                            createBookingInput,
                            calEventData
                        )
                    );
                } else {
                    onBookingSuccess(createDryRunBookingPayload());
                    logBookingInfo(
                        'Tried to create paid booking without calEventData',
                        {
                            additionalInfoToLog: bookerContext,
                        }
                    );
                }
            } catch (error) {
                logBookingError('Failed to create paid booking', {
                    errorMessage: (error as Error).message,
                    additionalInfoToLog: bookerContext,
                });
            } finally {
                resetBookerState();
                setConfirmButtonDisabled(false);
            }
        },
        [
            setConfirmButtonDisabled,
            editor,
            calEventData,
            resetBookerState,
            onBookingSuccess,
            bookerContext,
        ]
    );

    return useMemo(() => {
        const nativeEnabled = isNativeBookerEnabled;
        const hasSelectedHost = !!selectedHostId && selectedHostId !== -1;

        const pricingSection = <BookingPricingMetaSection />;
        const selectedHostSection = hasSelectedHost ? (
            <BookingSelectedHostMetaSection selectedHostId={selectedHostId} />
        ) : null;
        const backButton = !isLoading ? (
            <BookerBackButton
                calBookerStep={calBookerStep}
                containerClassName={
                    nativeEnabled
                        ? 'booking-atom-event-meta'
                        : 'event-meta-container'
                }
            />
        ) : null;

        // Native has dedicated meta slots (priceSlot / selectedProviderSlot);
        // the legacy atom has none, so those sections stay inline in
        // eventMetaChildren. One branch places each section in exactly one
        // spot — avoiding duplicate rows and scattered `nativeEnabled` checks.
        const metaLayout = nativeEnabled
            ? {
                  // The lib wraps priceSlot in a `.detail-row` div on
                  // truthiness alone, so a section that renders null still
                  // costs one `gap`. Keep the slot empty instead.
                  priceSlot: shouldShowPriceMetaSection(bookerContext)
                      ? pricingSection
                      : undefined,
                  selectedProviderSlot: selectedHostSection ?? undefined,
                  eventMetaChildren: <>{backButton}</>,
              }
            : {
                  priceSlot: undefined,
                  selectedProviderSlot: undefined,
                  eventMetaChildren: (
                      <>
                          {selectedHostSection}
                          {pricingSection}
                          {backButton}
                      </>
                  ),
              };

        const baseProps: CommonBookerProps = {
            isTeamEvent: true,
            teamId: teamId!,
            eventSlug: eventSlug || '',
            customClassNames: bookerClassNames,
            rescheduleClassNames,

            ...(shouldUsePaidBooking(bookerContext) && !rescheduleUid
                ? {
                      handleCreateBooking: handleCreatePaidBooking,
                      handleSlotReservation: async () => {
                          // no-op
                          // need to be passed to the booker atom to avoid internal reservation operation
                      },
                  }
                : { onCreateBookingSuccess: handleBookingSuccess }),
            onCreateBookingError: (resOrError: ApiErrorResponse | Error) => {
                logBookingError('Failed to create booking inside Booker atom', {
                    ...(resOrError instanceof Error
                        ? {
                              errorMessage: resOrError.message,
                              additionalInfoToLog: bookerContext,
                          }
                        : createBookerResponseLoggingContext(
                              bookerContext,
                              resOrError
                          )),
                });
            },
            onReserveSlotError: (res: ApiErrorResponse) => {
                logBookingError(
                    'Failed to reserve slot inside Booker atom',
                    createBookerResponseLoggingContext(bookerContext, res)
                );
            },
            onDeleteSlotError: (res: ApiErrorResponse) => {
                logBookingError(
                    'Failed to delete slot inside Booker atom',
                    createBookerResponseLoggingContext(bookerContext, res)
                );
            },
            showNoAvailabilityDialog: false,
            roundRobinHideOrgAndTeam: true,
            silentlyHandleCalendarFailures: true,
            metadata: getBookerMetadata(bookerContext, calEventData),
            confirmButtonDisabled,
            ...metaLayout,
        };

        if (hasSelectedHost) {
            baseProps.rrHostSubsetIds = [selectedHostId];
            baseProps.metadata = {
                ...baseProps.metadata,
                selectedHostId: selectedHostId.toString(),
            };
        }

        return baseProps;
    }, [
        teamId,
        eventSlug,
        bookerClassNames,
        rescheduleClassNames,
        bookerContext,
        rescheduleUid,
        handleCreatePaidBooking,
        handleBookingSuccess,
        isLoading,
        calBookerStep,
        calEventData,
        confirmButtonDisabled,
        selectedHostId,
        isNativeBookerEnabled,
    ]);
};
